import { useState, useEffect, useCallback, useMemo, useRef } from 'react';

import { GoogleMap } from '@react-google-maps/api';
import { observer } from 'mobx-react-lite';
import { MarkerClusterer } from '@googlemaps/markerclusterer';
import { useTranslation } from 'react-i18next';
import { ClusterRenderer } from '@bringg-frontend/bringg-map';

import { useStores } from 'bringg-web/recipes';
import { attachInfoWindow, closeInfoWindow } from 'bringg-web/features/service-area/info-window';
import AddressAutoComplete from 'bringg-web/components/address-auto-complete/address-auto-complete';
import { openPolygonPath } from 'bringg-web/features/service-area/utils/enclose-polygon';
import { simplifyPolygonPath } from 'bringg-web/features/service-area/utils/simplify-polygon-path';
import { useServiceAreaMapView, expandBounds } from 'bringg-web/features/service-area/use-service-area-map-view';
import { DecoratedServiceArea } from 'bringg-web/stores/service-areas-view/service-areas-view-store';

import './map.scss';

const DEFAULT_ZOOM = 12;
const OPACITY = 0.225;
const ZOOM_ON_TEAM = 15;
const ZOOM_ON_WORLD = 2;
// How much to expand the focused polygon's bounds to find neighbors during editing.
// 1.5 = expand 150% on each side → roughly 4x each dimension, so nearby polygons are visible.
const NEIGHBOR_BOUNDS_BUFFER = 1.5;

export type Props = {
	onPolygonCreate?: (polygon: google.maps.Polygon) => void;
	onPolygonEdit?: (polygon: google.maps.Polygon) => void;
	readonly?: boolean;
	mapCenter: { lat: number; lng: number } | null;
	teamId?: number;
};

export const ServiceAreaMap = observer(
	({ onPolygonCreate, onPolygonEdit, readonly = false, mapCenter, teamId = null }: Props) => {
		const { serviceAreaViewStore, teamsStore, merchantStore, serviceAreasStore } = useStores();
		const { t } = useTranslation();
		const [, setCluster] = useState<MarkerClusterer>(null);
		const [, setTeamCluster] = useState<MarkerClusterer>(null);
		// Ref instead of state: avoids component re-render when a new info-window
		// is created (which previously cascaded into cluster recreation).
		const infoWindowRef = useRef(new google.maps.InfoWindow());
		const { bounds: viewBounds, zoom: viewZoom } = useServiceAreaMapView(serviceAreaViewStore.map);

		// Polygon instances keyed by service area id (ref: no re-renders on show/hide)
		const polygonsRef = useRef<Map<number, google.maps.Polygon>>(new Map());
		// Service area data lookup for use by visibility/zoom effects
		const serviceAreaDataRef = useRef<Map<number, DecoratedServiceArea>>(new Map());
		// Track the zoom level at which polygons were last simplified
		const lastZoomRef = useRef<number>(0);
		// Track the previously focused service area id for diff-based focus updates
		const prevFocusedIdRef = useRef<number | null>(null);
		// Tracks whether initial map bounds have been applied (skip first fitBounds on filter change)
		const initialBoundsAppliedRef = useRef(false);
		const isDrawingRef = useRef(false);

		// All service areas with coordinate paths (used for clusters and polygon creation)
		const withPath = useMemo(
			() => serviceAreaViewStore.filtered.filter(serviceArea => serviceArea.path),
			[serviceAreaViewStore.filtered]
		);

		// Keep a ref to latest withPath for effects that need the data but should
		// NOT re-trigger when only colors change (e.g. cluster creation).
		const withPathRef = useRef(withPath);
		withPathRef.current = withPath;

		// Stable identity key: only changes when SAs are added/removed/filtered,
		// NOT when colors change.  Used to gate expensive cluster recreation.
		const clusterIds = useMemo(() => withPath.map(sa => sa.id).join(','), [withPath]);

		// Helper: clear path-level listeners then attach fresh ones.
		// Path events (set_at, insert_at) live on the MVCArray, not the Polygon,
		// so clearInstanceListeners(polygon) does NOT remove them.
		const attachPathListeners = useCallback(
			(polygon: google.maps.Polygon) => {
				google.maps.event.clearInstanceListeners(polygon.getPath());
				if (!onPolygonEdit) return;
				google.maps.event.addListener(polygon.getPath(), 'set_at', () => onPolygonEdit(polygon));
				google.maps.event.addListener(polygon.getPath(), 'insert_at', () => onPolygonEdit(polygon));
			},
			[onPolygonEdit]
		);

		// Fully tear down a polygon: detach path + polygon listeners, remove it
		// from the map, forget the entry in polygonsRef, and close the shared
		// InfoWindow. Closing the InfoWindow is mandatory because removing a
		// polygon while the cursor is over it means the `mouseout` handler
		// never fires, leaving the singleton InfoWindow open with stale content.
		const disposePolygon = useCallback((id: number, polygon: google.maps.Polygon) => {
			google.maps.event.clearInstanceListeners(polygon.getPath());
			google.maps.event.clearInstanceListeners(polygon);
			polygon.setMap(null);
			polygonsRef.current.delete(id);
			closeInfoWindow();
		}, []);

		// ─── Effect 1: Create / update polygons ───────────────────────────
		// Runs when data, filters, or focus changes (NOT on pan/zoom).
		// Uses diff-based updates: only creates new polygons, removes stale ones,
		// and updates existing ones in-place via setOptions/setPaths.
		useEffect(() => {
			if (!serviceAreaViewStore.map) return;

			const map = serviceAreaViewStore.map;
			const currentIds = new Set(withPath.map(sa => sa.id));
			const zoom = map.getZoom() || DEFAULT_ZOOM;
			const focusedId = serviceAreaViewStore.focusedServiceArea?.id ?? null;
			const prevFocusedId = prevFocusedIdRef.current;
			const focusChanged = focusedId !== prevFocusedId;

			// Remove polygons for service areas no longer present (filter/data change)
			for (const [id, polygon] of polygonsRef.current) {
				if (!currentIds.has(id)) {
					disposePolygon(id, polygon);
				}
			}

			// Create or update polygons
			for (const serviceArea of withPath) {
				let existing = polygonsRef.current.get(serviceArea.id);
				const isEditable = serviceArea.id === focusedId;

				// Force a fresh polygon + edit overlay every time edit mode is
				// entered. Toggling `editable` on a polygon that was previously
				// edited leaves Google Maps' edit overlay in a half-broken state
				// https://bringg.atlassian.net/browse/BRNGG-54208
				if (existing && focusChanged && serviceArea.id === focusedId) {
					disposePolygon(serviceArea.id, existing);
					existing = undefined;
				}

				if (existing) {
					// ── Update existing polygon in-place ──
					if (existing.getMap() !== map) {
						existing.setMap(map);
					}

					existing.setOptions({
						fillColor: serviceArea.color,
						strokeColor: serviceArea.color,
						fillOpacity: OPACITY,
						editable: isEditable
					});

					if (focusChanged && serviceArea.id === prevFocusedId) {
						// Polygon just lost focus -- swap back to the simplified path
						// for performance.
						google.maps.event.clearInstanceListeners(existing.getPath());
						const simplifiedPath = simplifyPolygonPath(serviceArea.path, zoom, false);
						existing.setPaths(openPolygonPath(simplifiedPath));
					}
				} else {
					// ── Create new polygon ──
					const simplifiedPath = simplifyPolygonPath(serviceArea.path, zoom, isEditable);
					const polygon = new google.maps.Polygon({
						map,
						paths: openPolygonPath(simplifiedPath),
						fillColor: serviceArea.color,
						strokeColor: serviceArea.color,
						fillOpacity: OPACITY,
						editable: isEditable
					});

					const serviceAreaId = serviceArea.id;
					const iw = attachInfoWindow({
						serviceArea,
						t,
						polygon,
						readonly,
						isEditSessionActive: () =>
							Boolean(serviceAreaViewStore.focusedServiceArea) || isDrawingRef.current,
						onEdit: () => {
							// do not use serviceArea as referecne here because it is not the latest data
							const currentServiceArea = serviceAreaDataRef.current.get(serviceAreaId);

							serviceAreaViewStore.focusServiceArea(currentServiceArea);
						}
					});
					infoWindowRef.current = iw;

					attachPathListeners(polygon);
					polygonsRef.current.set(serviceArea.id, polygon);
				}
			}

			// Update refs for other effects
			serviceAreaDataRef.current = new Map(withPath.map(sa => [sa.id, sa]));
			lastZoomRef.current = zoom;
			prevFocusedIdRef.current = focusedId;
		}, [
			withPath,
			serviceAreaViewStore.map,
			serviceAreaViewStore.focusedServiceArea?.polygon,
			serviceAreaViewStore.focusedServiceArea?.id,
			t,
			onPolygonEdit,
			readonly,
			attachPathListeners,
			disposePolygon
		]);

		// ─── Effect 2: Viewport visibility (trivial, runs on pan/focus) ────
		// Toggles polygon visibility via setVisible() -- no create/destroy.
		// In edit mode: shows the focused polygon + spatial neighbors (polygons
		// whose bounds intersect the focused polygon's expanded bounds).
		useEffect(() => {
			if (!polygonsRef.current.size) return;

			const focusedId = serviceAreaViewStore.focusedServiceArea?.id;

			// Edit mode: show focused polygon + neighbors within expanded bounds
			if (focusedId) {
				const focusedSa = serviceAreaDataRef.current.get(focusedId);
				const neighborhoodBounds = focusedSa?.pathBounds
					? expandBounds(focusedSa.pathBounds, NEIGHBOR_BOUNDS_BUFFER)
					: null;

				for (const [id, polygon] of polygonsRef.current) {
					if (id === focusedId) {
						polygon.setVisible(true);
						continue;
					}

					// Show neighbor if its bounds intersect the expanded focused bounds
					const sa = serviceAreaDataRef.current.get(id);
					const isNeighbor =
						neighborhoodBounds && sa?.pathBounds && neighborhoodBounds.intersects(sa.pathBounds);
					polygon.setVisible(Boolean(isNeighbor));
				}
				return;
			}

			// Normal mode: viewport-based visibility
			if (!viewBounds) return;

			for (const [id, polygon] of polygonsRef.current) {
				const sa = serviceAreaDataRef.current.get(id);
				const isVisible = !sa?.pathBounds || viewBounds.intersects(sa.pathBounds);
				polygon.setVisible(isVisible);
			}
		}, [viewBounds, serviceAreaViewStore.focusedServiceArea?.id]);

		// ─── Effect 3: Zoom re-simplification (moderate, runs on zoom) ────
		// Updates polygon paths when zoom level changes, using setPaths() in-place.
		useEffect(() => {
			if (!viewZoom || lastZoomRef.current === viewZoom) return;

			const focusedId = serviceAreaViewStore.focusedServiceArea?.id;
			lastZoomRef.current = viewZoom;

			for (const [id, polygon] of polygonsRef.current) {
				// Skip the editable polygon (needs full detail, not simplified)
				if (id === focusedId) continue;

				// Only update visible polygons (invisible ones get updated when they become visible
				// on the next zoom effect run, since zoom change always updates lastZoomRef)
				if (!polygon.getVisible()) continue;

				const sa = serviceAreaDataRef.current.get(id);
				if (!sa?.path) continue;

				const simplifiedPath = simplifyPolygonPath(sa.path, viewZoom, false);
				polygon.setPaths(openPolygonPath(simplifiedPath));
			}
		}, [viewZoom, serviceAreaViewStore.focusedServiceArea?.id]);

		// ─── Cleanup on unmount ───────────────────────────────────────────
		useEffect(() => {
			const polygons = polygonsRef.current;
			const data = serviceAreaDataRef.current;
			return () => {
				for (const [id, polygon] of Array.from(polygons)) {
					disposePolygon(id, polygon);
				}
				data.clear();
			};
		}, [disposePolygon]);

		// ─── Effect: Render marker clusters ─────────────────────────────
		// Depends on clusterIds (SA id list) instead of withPath to avoid
		// destroying/recreating the MarkerClusterer on color-only changes
		// (e.g. focus cleared after save).  Uses withPathRef for data access.
		useEffect(() => {
			if (!serviceAreaViewStore.map) {
				return;
			}

			setCluster(prev => {
				if (prev) {
					prev.setMap(null);
				}

				const currentWithPath = withPathRef.current;
				return new MarkerClusterer({
					map: serviceAreaViewStore.map,
					renderer: new ClusterRenderer(infoWindowRef.current, { cluster: true }, serviceAreaViewStore.map),
					markers: currentWithPath.map(
						sa =>
							new google.maps.Marker({
								visible: true,
								position: sa.center,
								opacity: 0
							})
					)
				});
			});
		}, [serviceAreaViewStore, serviceAreaViewStore.map, clusterIds]);

		// ─── Effect: Render team markers (clustered) ────────────────────
		// Separate from SA clusters: depends on teams data and filters, not on SA list.
		// Uses MarkerClusterer to avoid hundreds of individual DOM markers.
		useEffect(() => {
			if (!serviceAreaViewStore.map) {
				return;
			}

			setTeamCluster(prev => {
				if (prev) {
					prev.setMap(null);
				}

				let teamsToRender;
				if (teamId) {
					teamsToRender = teamsStore.teams.get(teamId) ? [teamsStore.teams.get(teamId)] : [];
				} else {
					teamsToRender = teamsStore.all;
				}

				// Respect the active teams filter so markers match filtered service areas
				const byTeams = serviceAreaViewStore.filters?.byTeams;
				if (byTeams?.length) {
					teamsToRender = teamsToRender.filter(team => byTeams.includes(team.id));
				}

				const markers = teamsToRender
					.filter(team => team.lat && team.lng)
					.map(team => {
						const marker = new google.maps.Marker({
							title: team.name,
							position: team,
							icon: {
								url: '/images/team_home_v2.png',
								scaledSize: new google.maps.Size(40, 40)
							}
						});

						marker.addListener('click', () => {
							serviceAreaViewStore.map.setCenter(marker.getPosition());
							serviceAreaViewStore.map.setZoom(ZOOM_ON_TEAM);
						});

						return marker;
					});

				return new MarkerClusterer({
					map: serviceAreaViewStore.map,
					markers
				});
			});
		}, [serviceAreaViewStore, serviceAreaViewStore.map, serviceAreaViewStore.filters, teamsStore, teamId]);

		const onLoad = map => {
			if (!map) {
				return;
			}

			if (!readonly) {
				// DrawingManager was removed from Maps JS API v3.65+ (INC-3414) - if it throws, keep the map alive without the draw tool
				try {
					const drawingManager = new google.maps.drawing.DrawingManager({
						map,
						drawingControlOptions: {
							position: google.maps.ControlPosition.TOP_LEFT,
							drawingModes: [google.maps.drawing.OverlayType.POLYGON]
						}
					});

					google.maps.event.addListener(drawingManager, 'drawingmode_changed', () => {
						isDrawingRef.current =
							drawingManager.getDrawingMode() === google.maps.drawing.OverlayType.POLYGON;
					});

					google.maps.event.addListener(drawingManager, 'polygoncomplete', polygon => {
						drawingManager.setDrawingMode(null);
						onPolygonCreate(polygon);
					});
				} catch (error) {
					console.error('DrawingManager unavailable, service area drawing disabled', error);
				}
			}

			serviceAreaViewStore.onMapLoad(map);
		};

		useEffect(() => {
			if (!serviceAreaViewStore.map) {
				return;
			}

			if (serviceAreaViewStore.bounds) {
				serviceAreaViewStore.centerMap();
			} else {
				serviceAreaViewStore.map.setZoom(mapCenter ? DEFAULT_ZOOM : ZOOM_ON_WORLD);
				serviceAreaViewStore.map.setCenter(mapCenter ? mapCenter : { lat: 0, lng: 0 });
			}
		}, [serviceAreaViewStore.map, mapCenter]);

		// ─── Effect: Fit map bounds when team filter changes ────────────
		// When the user applies/changes a team filter, adjust the viewport to
		// encompass all currently visible polygons.  Depends on filters (not
		// clusterIds) so that adding/removing a single SA (create/delete) does
		// NOT trigger a full re-fit.
		useEffect(() => {
			if (!serviceAreaViewStore.map) {
				return;
			}

			if (!initialBoundsAppliedRef.current) {
				initialBoundsAppliedRef.current = true;
				return;
			}

			if (serviceAreaViewStore.bounds) {
				serviceAreaViewStore.centerMap();
			}
		}, [serviceAreaViewStore.map, serviceAreaViewStore.filters]);

		const onSearch = useCallback(
			async (address, _, placeId) => {
				if (!serviceAreaViewStore.map) {
					return;
				}

				const service = new google.maps.places.PlacesService(serviceAreaViewStore.map);
				const viewportPromise: Promise<google.maps.LatLngBounds> = new Promise(resolve => {
					service.getDetails({ placeId, fields: ['geometry'] }, (place, status) => {
						if (status === google.maps.places.PlacesServiceStatus.OK) {
							resolve(place.geometry.viewport);
						}
					});
				});

				serviceAreaViewStore.map.fitBounds(await viewportPromise);
			},
			[serviceAreaViewStore.map]
		);

		if (!merchantStore.merchant || !serviceAreasStore.isFetched || !teamsStore.isFetched) {
			return null;
		}

		return (
			<>
				<AddressAutoComplete
					data-test-id="google-autocomplete"
					placeholder={t('GLOBAL.SEARCH')}
					onSelect={onSearch}
					className="service-area-map-autocomplete"
				/>
				<GoogleMap
					data-test-id="google-maps-component"
					options={{
						mapId: 'bringg-map',
						streetViewControl: false,
						mapTypeControl: false,
						fullscreenControl: false
					}}
					onLoad={onLoad}
					mapContainerClassName="service-area-map"
				/>
			</>
		);
	}
);
